/**
 * RequireRole — the ONLY place the frontend authorization decision is made (ADR-5).
 *
 * How it resolves the effective role:
 *   - Dev mode (devAuthEnabled === true):  reads the dev role from localStorage
 *     via isDevReviewerOrAdmin() from config/devAuth.
 *   - Production (devAuthEnabled === false): derives from the active MSAL account's
 *     ID-token claims.  Checks `roles` claim first (Entra app-role assignment);
 *     falls back to checking `groups` against the configured group IDs (note: groups
 *     overage suppresses this — acceptable for v1 with small reviewer groups).
 *
 * Children render only when the effective role is reviewer OR admin.
 * All other cases render the `fallback` prop (defaults to <AccessDeniedPage />).
 *
 * This component is NOT a router guard — it is a render-time guard.  F4's
 * router wraps the /review routes in <RequireRole> so the check happens on
 * every render, not just on navigation.
 */

import type { ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
  devAuthEnabled,
  devRoleOverrideEnabled,
  isDevReviewerOrAdmin,
  getDevRoleOverride,
} from '@/config/devAuth';
import { AccessDeniedPage } from '@/pages/AccessDeniedPage';

interface RequireRoleProps {
  /** Content to render when the effective role grants access. */
  children: ReactNode;
  /**
   * What to render when access is denied.
   * Defaults to <AccessDeniedPage /> — callers can override for custom fallbacks.
   */
  fallback?: ReactNode;
}

// ---------------------------------------------------------------------------
// Production role derivation (best-effort until real Entra values arrive).
// ---------------------------------------------------------------------------

const REVIEWER_ROLE = 'Reviewer';
const ADMIN_ROLE = 'Admin';

/**
 * Derive whether the MSAL account holds a reviewer-or-admin role.
 *
 * Claim priority:
 *   1. `roles` array claim (Entra "App roles" — preferred, explicit).
 *   2. `groups` array claim — matched against the configured group IDs.
 *
 * Returns false (deny) when:
 *   - No active account.
 *   - Token claims are absent or unparseable.
 *   - Neither claim type grants the role.
 */
function msalIsReviewerOrAdmin(idTokenClaims: Record<string, unknown> | undefined): boolean {
  if (!idTokenClaims) return false;

  // 1. App-role claim.
  const roles = idTokenClaims['roles'];
  if (Array.isArray(roles)) {
    if (roles.includes(REVIEWER_ROLE) || roles.includes(ADMIN_ROLE)) return true;
  }

  // 2. Groups claim (group object IDs — values come from VITE_ env at build time).
  const reviewerGroupId = import.meta.env.VITE_AZURE_REVIEWER_GROUP_ID as string | undefined;
  const adminGroupId = import.meta.env.VITE_AZURE_ADMIN_GROUP_ID as string | undefined;
  const groups = idTokenClaims['groups'];
  if (Array.isArray(groups)) {
    if (reviewerGroupId && groups.includes(reviewerGroupId)) return true;
    if (adminGroupId && groups.includes(adminGroupId)) return true;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RequireRole({ children, fallback }: RequireRoleProps) {
  const { accounts, inProgress } = useMsal();
  const activeAccount = accounts[0];

  // MsalProvider initializes with accounts:[] and inProgress:Startup, then
  // re-runs handleRedirectPromise before settling. Returning null here prevents
  // the fallback from firing (and triggering a redirect) before the session
  // is restored from cache.
  if (!devAuthEnabled && inProgress === InteractionStatus.Startup) {
    return null;
  }

  let hasAccess: boolean;

  if (devAuthEnabled) {
    // Full MSAL bypass: derive from localStorage-backed dev role.
    hasAccess = isDevReviewerOrAdmin();
  } else if (devRoleOverrideEnabled) {
    // UI-only override: real MSAL auth, but role display overridden locally.
    const override = getDevRoleOverride();
    if (override !== null) {
      hasAccess = override === 'reviewer' || override === 'admin';
    } else {
      // No override ("auto") — use real token claims.
      const claims = activeAccount?.idTokenClaims as Record<string, unknown> | undefined;
      hasAccess = msalIsReviewerOrAdmin(claims);
    }
  } else {
    // Production: derive from the active MSAL account's ID-token claims.
    const claims = activeAccount?.idTokenClaims as Record<string, unknown> | undefined;
    hasAccess = msalIsReviewerOrAdmin(claims);
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  // When a fallback is explicitly provided (e.g. router.tsx passes one) honour it
  // only if the user is not signed in — prevents a redirect loop when a dev role
  // override blocks an otherwise-authenticated user.
  if (fallback !== undefined) {
    const isSignedIn = devAuthEnabled || accounts.length > 0;
    if (!isSignedIn) return <>{fallback}</>;
  }

  return <AccessDeniedPage />;
}

export default RequireRole;
